import { useDispatch, useSelector } from "react-redux";

import ItemIcon from "components/base/ItemIcon";
import {
  areUpgradeRequirementsMet,
  getInventoryUpgradeStorage,
  getUpgradeData,
} from "libs/item";
import { getPartData } from "libs/stats";
import { installUpgrade, storeUpgrade, unstoreUpgrade } from "redux/actions";
import {
  getStorageUpgrades,
  getInstalledUpgrades,
  getTotalBaseStats,
} from "redux/selectors/characterSelectors";
import { PartModel } from "types";
import { COLORS } from "utils/constants";
import Button from "./Button";
import {
  InventoryActionIcon,
  Item,
  ItemActions,
  ItemButtons,
  ItemImageWrapper,
  ItemInfo,
  ItemInstalled,
  ItemRestricted,
  ItemTitle,
  ItemWrapper,
} from "./ItemBase";
import ItemName from "./ItemName";
import Tooltip, { TooltipDesc, TooltipName } from "./Tooltip";

interface InventoryUpgradeItemProps {
  slug: string;
  atShipworks: boolean;
  onHover?: (upgradeSlug: string | null) => void;
}

const StoreTooltip = ({ upgradeName }: { upgradeName: string }) => (
  <>
    <TooltipName>Move to Storage</TooltipName>
    <TooltipDesc>
      Move {upgradeName} to storage to keep your inventory clean.
    </TooltipDesc>
  </>
);

const UnstoreTooltip = ({ upgradeName }: { upgradeName: string }) => (
  <>
    <TooltipName>Move out of Storage</TooltipName>
    <TooltipDesc>
      Move {upgradeName} out of storage and back into your inventory.
    </TooltipDesc>
  </>
);

const InstallTooltip = ({
  upgradeName,
  partData,
}: {
  upgradeName: string;
  partData: PartModel;
}) => (
  <>
    <TooltipName>Install {partData.name}</TooltipName>
    <TooltipDesc>
      Install {upgradeName} into your ship's {partData.name} slot.
    </TooltipDesc>
  </>
);

const LockedTooltip = ({ upgradeName }: { upgradeName: string }) => (
  <>
    <TooltipName>Unable to Install</TooltipName>
    <TooltipDesc>
      Your ship doesn't have the required base stats to install {upgradeName}.
    </TooltipDesc>
  </>
);

export default function InventoryUpgradeItem({
  slug,
  atShipworks,
  onHover,
}: InventoryUpgradeItemProps) {
  const dispatch = useDispatch();

  const totalBaseStats = useSelector(getTotalBaseStats);
  const installedUpgrades = useSelector(getInstalledUpgrades);
  const storage = useSelector(getStorageUpgrades);

  const upgradeData = getUpgradeData(slug);
  const partData = getPartData(upgradeData.part);
  const inStorage = getInventoryUpgradeStorage(slug, storage);
  const isRestricted = !areUpgradeRequirementsMet(slug, totalBaseStats);
  const isInstalled = installedUpgrades[upgradeData.part] === slug;

  return (
    <Item
      key={slug}
      onMouseEnter={() => !isInstalled && onHover?.(slug)}
      onMouseLeave={() => onHover?.(null)}
    >
      <ItemWrapper>
        <ItemImageWrapper
          isRestricted={isRestricted}
          isInstalled={isInstalled}
          partColor={partData.color}
        >
          <ItemIcon slug={slug} />
        </ItemImageWrapper>
        <ItemInfo>
          <ItemTitle isRestricted={isRestricted}>
            <ItemName slug={slug} />
          </ItemTitle>
          {isInstalled && (
            <Tooltip
              content={
                <>
                  <TooltipName>Installed {partData.name}</TooltipName>
                  <TooltipDesc>
                    {upgradeData.name} is currently installed in your ship's{" "}
                    {partData.name} slot.
                  </TooltipDesc>
                </>
              }
              color={partData.color}
            >
              <ItemInstalled partColor={partData.color}>✦</ItemInstalled>
            </Tooltip>
          )}
          {isRestricted && (
            <Tooltip
              content={<LockedTooltip upgradeName={upgradeData.name} />}
              color={COLORS.RESTRICTED}
            >
              <ItemRestricted>♢</ItemRestricted>
            </Tooltip>
          )}
        </ItemInfo>
      </ItemWrapper>
      <ItemActions>
        <ItemButtons>
          {!inStorage ? (
            <Tooltip
              isDisabled={isInstalled}
              content={<StoreTooltip upgradeName={upgradeData.name} />}
            >
              <Button
                $style="secondary"
                $size="xsmall"
                $disabled={isInstalled}
                onClick={() => dispatch(storeUpgrade(slug))}
              >
                ❒
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              content={<UnstoreTooltip upgradeName={upgradeData.name} />}
            >
              <Button
                $style="secondary"
                $size="xsmall"
                onClick={() => dispatch(unstoreUpgrade(slug))}
              >
                ❏
              </Button>
            </Tooltip>
          )}
          {!!atShipworks && !inStorage && (
            <Tooltip
              isDisabled={isRestricted || isInstalled}
              content={
                <InstallTooltip
                  upgradeName={upgradeData.name}
                  partData={partData}
                />
              }
              color={partData.color}
            >
              <Button
                $style="normal"
                $size="xsmall"
                $disabled={isRestricted || isInstalled}
                onClick={() => dispatch(installUpgrade(slug))}
              >
                <InventoryActionIcon />
              </Button>
            </Tooltip>
          )}
        </ItemButtons>
      </ItemActions>
    </Item>
  );
}
